import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { exportTreeToJSON } from '../utils/treeExporter';

export type SaveStatus = 'unsaved' | 'tainted' | 'saved' | 'saving';

interface SaveStatusContextType {
  saveStatus: SaveStatus;
  lastSavedTime: Date | null;
  currentTime: Date;
}

const SaveStatusContext = createContext<SaveStatusContextType | undefined>(undefined);

interface SaveStatusProviderProps {
  children: ReactNode;
}

export const SaveStatusProvider: React.FC<SaveStatusProviderProps> = ({ children }) => {
  const { state, setSaveStatus: setGlobalSaveStatus } = useWhiteboardStore();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('unsaved');
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Generate current state hash for comparison
  const getCurrentStateHash = () => {
    try {
      return exportTreeToJSON(state, { forComparison: true });
    } catch (error) {
      console.warn('Failed to generate state hash:', error);
      return JSON.stringify(state);
    }
  };

  // Update current time every minute for accurate timestamps
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Check if state has changed
  useEffect(() => {
    const currentStateHash = getCurrentStateHash();
    
    if (!state.hasEverBeenSaved) {
      setSaveStatus('unsaved');
    } else if (state.lastSavedStateHash && currentStateHash === state.lastSavedStateHash) {
      setSaveStatus('saved');
    } else {
      setSaveStatus('tainted');
    }
  }, [state]);

  // Listen for save events from the global scope
  useEffect(() => {
    const handleSaveEvent = () => {
      const currentStateHash = getCurrentStateHash();
      setGlobalSaveStatus(currentStateHash);
      setLastSavedTime(new Date());
    };

    const handleLoadEvent = () => {
      // Don't overwrite the save status if it's already been set by loadTree
      // The loadTree function already sets lastSavedStateHash and hasEverBeenSaved
      // Reset the last saved time when loading a new canvas
      setLastSavedTime(null);
    };

    const handleSavingEvent = () => {
      setSaveStatus('saving');
    };

    // Listen for custom save/load events
    window.addEventListener('whiteboard-saved', handleSaveEvent as EventListener);
    window.addEventListener('whiteboard-loaded', handleLoadEvent as EventListener);
    window.addEventListener('whiteboard-saving', handleSavingEvent as EventListener);

    return () => {
      window.removeEventListener('whiteboard-saved', handleSaveEvent as EventListener);
      window.removeEventListener('whiteboard-loaded', handleLoadEvent as EventListener);
      window.removeEventListener('whiteboard-saving', handleSavingEvent as EventListener);
    };
  }, [getCurrentStateHash, setGlobalSaveStatus]);

  const contextValue: SaveStatusContextType = {
    saveStatus,
    lastSavedTime,
    currentTime
  };
  
  return (
    <SaveStatusContext.Provider value={contextValue}>
      {children}
    </SaveStatusContext.Provider>
  );
};

export const useSaveStatus = (): SaveStatusContextType => {
  const context = useContext(SaveStatusContext);
  if (context === undefined) {
    throw new Error('useSaveStatus must be used within a SaveStatusProvider');
  }
  return context;
};
